import React, { useState, useCallback } from 'react';
import { Canvas } from '@react-three/fiber';
import CustomButton from '../ui/Button';
import { useSolutionDropdown } from '../hooks/SolutionDropdownContext';
import { useTranslation } from "react-i18next";
import { OrbitControls } from '@react-three/drei';
import { Scene } from './Scene';
import { Trans } from 'react-i18next';

const HeroSection = () => {
    const { t, i18n } = useTranslation("home");
    const isRussian = i18n.language === "ru";
    const { toggleDropdown } = useSolutionDropdown();
    const [hoverPoint, setHoverPoint] = useState(null);
    const [scrollY, setScrollY] = useState(0);
    const [hasScrolled, setHasScrolled] = useState(false);

    const handleMouseMove = useCallback((event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        setHoverPoint({ x, y });
    }, []);

    React.useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY);
            if (!hasScrolled) {
                setHasScrolled(true);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [hasScrolled]);

    return (
        <div className={`bg-[#171717] relative lg:h-[720px] md:h-[900px] sm:h-[900px] h-[1220px]  min-[380px]:h-[1100px]`} onMouseMove={handleMouseMove}>
            <div className="absolute inset-0">
                <Canvas
                    camera={{
                        position: [15, 12, 15],
                        fov: 45,
                        near: 0.1,
                        far: 1000
                    }}
                    className="w-full h-full"
                >
                    <Scene scrollY={scrollY} />
                    <OrbitControls
                        enableZoom={false}
                        enablePan={false}
                        enableRotate={false}
                        autoRotate={hasScrolled}
                        autoRotateSpeed={hasScrolled ? 1 : 0}
                    />
                    <fog attach="fog" args={['#000000', 15, 35]} />
                </Canvas>
            </div>

            <div className="absolute inset-0 bg-[#00000066]"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-2">
                <div className='max-w-[1140px] lg:px-[21px] px-2'>
                    <h1 className={"montserrat-font text-center md:text-[24px] text-[20px] leading-[normal] font-bold mb-6 md:mb-8"}>
                        <Trans ns="home" i18nKey="subtitle" components={[<b className="gradient-text" />]} />
                    </h1>
                    <h2 className="geologica-font text-center lg:text-[40px] md:text-[36px] sm:text-[28px] text-[22px] leading-[normal] font-bold mb-6 md:mb-8 min-[360px]:text-[24px]">
                        <Trans ns="home" i18nKey="title" components={[<b className="gradient-text" />]} />
                    </h2>
                    <ul className="flex w-full md:flex-row flex-col gap-[21px] flex-wrap justify-center mb-6 md:mb-8">
                        {Array.from({ length: 4 }).map((_, index) => (
                            <li
                                key={index}
                                className="rounded-2 py-6 px-3 border-[rgba(255,255,255,0.33)] border-[1px] rounded-[16px] flex md:flex-col flex-row md:items-center items-start gap-[10px] md:justify-center justify-start w-full md:w-[calc(50%-21px)] lg:w-[calc(25%-21px)] md:max-w-[330px]"
                            >
                                <img src="/assets/alert-line.svg" alt="Alert icon" className='w-[24px] h-[24px]' />
                                <span className="text-color-2 montserrat-font text-[14px] font-semibold md:text-center text-left leading-[normal]">
                                    {t(`customer_pain_${index + 1}`)}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <p className="md:text-[24px] text-[20px] text-center font-bold leading-[normal] montserrat-font mb-6 md:mb-8">
                        <Trans
                            ns="home"
                            i18nKey="pains_solution"
                            components={[
                            <b className="gradient-text" />,
                            <span className="font-normal" />,
                            ]}
                        />
                    </p>
                </div>
                <div className="flex flex-col md:flex-row gap-4 md:gap-0">
                    <CustomButton>
                    <a href="/contact_us">
                            <div className="flex items-center justify-center gap-2">
                                {t("start_free_trial")}
                            </div>
                        </a>
                    </CustomButton>
                    <CustomButton transparent onClick={toggleDropdown}>
                        <div className="flex items-center justify-center gap-2">
                            {t("explore_solution")}
                        </div>
                    </CustomButton>
                </div>
            </div>
        </div>
    );
};

export default HeroSection;